import React, { useState, useEffect, useCallback } from "react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger, DialogFooter } from "@/components/ui/dialog";
import { Label } from "@/components/ui/label";
import { Users, Info, AlertCircle, CheckCircle2, FileWarning, CreditCard, Copy, Sparkles } from "lucide-react";
import { 
  formatPhoneOnInput, 
  capitalizeFirst, 
  formatCodiceFiscaleOnInput,
  formatPartitaIVAOnInput,
  validateCodiceFiscale,
  validatePartitaIVA
} from "@/utils/helpers";
import { analyzeCF, isValidCFFormat } from "@/utils/codiceFiscale";
import { cn } from "@/lib/utils";
import { confirmDirtyClose, useFormDirty } from "@/lib/dialogConfirm";
import { toast } from "sonner";
import api from "@/api";
import { Client360Dialog } from "@/components/dialogs/Client360Dialog";

// Auto-format date: 01012025 → 01/01/2025
const formatDateOnInput = (value) => {
  // Remove non-digits
  const digits = value.replace(/\D/g, '');
  
  // Apply formatting as user types
  if (digits.length <= 2) {
    return digits;
  } else if (digits.length <= 4) {
    return `${digits.slice(0, 2)}/${digits.slice(2)}`;
  } else {
    return `${digits.slice(0, 2)}/${digits.slice(2, 4)}/${digits.slice(4, 8)}`;
  }
};

// Helper to handle field updates with auto-capitalization and phone formatting
const handleFieldUpdate = (clientForm, setClientForm, field, value, setErrors) => {
  let processedValue = value;
  
  // Auto-capitalize text fields
  const textFields = ["fullName", "street", "city"];
  if (textFields.includes(field) && value) {
    processedValue = capitalizeFirst(value);
  }
  
  // Format phone number
  if (field === "phone" && value) {
    processedValue = formatPhoneOnInput(value);
  }
  
  // Format Codice Fiscale
  if (field === "taxCode" && value) {
    processedValue = formatCodiceFiscaleOnInput(value);
  }
  
  // Format Partita IVA
  if (field === "vatNumber" && value) {
    processedValue = formatPartitaIVAOnInput(value);
  }
  
  // Province uppercase
  if (field === "province" && value) {
    processedValue = value.toUpperCase().slice(0, 2);
  }
  
  // CAP numbers only
  if (field === "postalCode" && value) {
    processedValue = value.replace(/\D/g, '').slice(0, 5);
  }
  
  // Document number uppercase
  if (field === "documentNumber" && value) {
    processedValue = value.toUpperCase();
  }
  
  // Auto-format date fields (documentIssueDate, documentExpiryDate)
  if ((field === "documentIssueDate" || field === "documentExpiryDate") && value) {
    processedValue = formatDateOnInput(value);
  }
  
  setClientForm({ ...clientForm, [field]: processedValue });
  
  // Clear error when user starts typing
  if (setErrors) {
    setErrors(prev => ({ ...prev, [field]: null }));
  }
};

export function ClientDialog({
  isOpen,
  onOpenChange,
  editingClient,
  clientForm,
  setClientForm,
  onSave,
  onReset,
  isSaving
}) {
  // Validation errors state
  const [errors, setErrors] = useState({});
  // CF analysis result
  const [cfAnalysis, setCfAnalysis] = useState(null);
  // Duplicate detection state (26/02/2026).
  //  - dupMatches: array di match (taxCode | vatNumber | name) dal backend.
  //  - dupBlocking: true se almeno un match è su taxCode/vatNumber.
  //  Edit mode: l'id del cliente corrente viene escluso dal check.
  const [dupMatches, setDupMatches] = useState([]);
  const [dupBlocking, setDupBlocking] = useState(false);

  // CAP (ZIP) auto-fill state (28/02/2026):
  //  - capSuggestions: lista [{city, province}] in caso di multi-match
  //  - capWarning: messaggio non-bloccante quando il CAP non esiste in DB
  //  - capLastLookup: ultimo CAP per cui abbiamo fatto la lookup (debounce)
  // L'auto-fill rispetta i campi manualmente compilati: città/provincia
  // NON vengono sovrascritti se già contengono un valore diverso da quello
  // associato al CAP precedente (rispetto della scelta operatore).
  const [capSuggestions, setCapSuggestions] = useState([]);
  const [capWarning, setCapWarning] = useState("");
  const [capLastLookup, setCapLastLookup] = useState("");

  // Cliente 360° dialog state (17/07/2026 — v1.4.1). La modale è
  // indipendente e viene aperta dal footer solo in modalità modifica
  // (serve un clientId reale per la fetch della timeline aggregata).
  const [is360Open, setIs360Open] = useState(false);

  // Determine if we should show business fields (VAT, SDI, PEC)
  const showBusinessFields = clientForm.clientType !== "Privato";
  
  // Determine if CF should be numeric-only (for Azienda)
  const isCompanyType = clientForm.clientType === "Azienda";

  // REA field visibility: solo per Azienda e Ditta individuale.
  const showReaField = clientForm.clientType === "Azienda" || clientForm.clientType === "Ditta individuale";
  
  // Validate CF based on client type
  const validateCFForClientType = useCallback((cf) => {
    if (!cf) return { valid: true };
    
    if (isCompanyType) {
      // For Azienda: CF must be numeric (11 digits, same as P.IVA)
      const numericCF = cf.replace(/\D/g, '');
      if (numericCF.length !== 11) {
        return { valid: false, error: 'Codice Fiscale azienda deve essere di 11 cifre' };
      }
      return { valid: true };
    } else {
      // For other types: standard alphanumeric CF validation
      return validateCodiceFiscale(cf);
    }
  }, [isCompanyType]);
  
  // Analyze CF when it changes and auto-fill birth data (only for person types)
  // 17/07/2026 — v1.4.1: passiamo lastName + firstName ESPLICITI così i
  // cognomi/nomi con spazio interno (DI GIOVANNI, DE MARCO, LO STORTO,
  // DEL VECCHIO, MARIA GRAZIA, …) NON vengono splittati erroneamente.
  // Passiamo anche birthPlace per il check di consistenza CF↔città.
  const handleCFAnalysis = useCallback((cf, fullName, lastName, firstName, birthPlaceInput) => {
    // Skip analysis for company types (numeric CF)
    if (isCompanyType) {
      setCfAnalysis(null);
      return;
    }
    
    if (!cf || cf.length < 16) {
      setCfAnalysis(null);
      return;
    }
    
    const analysis = analyzeCF(cf, fullName, lastName, firstName, birthPlaceInput);
    setCfAnalysis(analysis);
    
    // Auto-fill birth data if extraction is reliable
    // Always fill if we have valid data and field is empty
    if (analysis.valid) {
      const updates = {};
      
      // Auto-fill birthDate if available and field is empty
      if (analysis.birthDate && !clientForm.birthDate) {
        updates.birthDate = analysis.birthDate;
      }
      
      // Auto-fill birthPlace if available and field is empty
      if (analysis.birthPlace && !clientForm.birthPlace) {
        updates.birthPlace = analysis.birthPlace;
      }
      
      // Apply updates if any
      if (Object.keys(updates).length > 0) {
        setClientForm(prev => ({
          ...prev,
          ...updates
        }));
      }
    }
  }, [clientForm.birthDate, clientForm.birthPlace, setClientForm, isCompanyType]);
  
  // Watch for CF changes
  useEffect(() => {
    if (clientForm.taxCode && clientForm.taxCode.length >= 16) {
      handleCFAnalysis(
        clientForm.taxCode,
        clientForm.fullName,
        clientForm.lastName,
        clientForm.firstName,
        clientForm.birthPlace,
      );
    }
  }, [clientForm.taxCode, clientForm.fullName, clientForm.lastName, clientForm.firstName, clientForm.birthPlace, handleCFAnalysis]);

  // Real-time duplicate detection (debounced 400ms).
  // Triggers su qualsiasi cambio di taxCode/vatNumber/fullName.
  // In edit mode passa `excludeId` per non auto-matchare.
  useEffect(() => {
    if (!isOpen) return;
    const tax = (clientForm.taxCode || "").trim();
    const vat = (clientForm.vatNumber || "").trim();
    const name = (clientForm.fullName || "").trim();
    // No-op se tutti vuoti.
    if (!tax && !vat && !name) {
      setDupMatches([]); setDupBlocking(false);
      return;
    }
    const t = setTimeout(() => {
      api.checkClientDuplicate({
        taxCode: tax,
        vatNumber: vat,
        fullName: name,
        excludeId: editingClient?.id || null,
      })
        .then((res) => {
          setDupMatches(Array.isArray(res?.matches) ? res.matches : []);
          setDupBlocking(Boolean(res?.blocking));
        })
        .catch(() => { /* silent — la UI continua a funzionare anche se l'endpoint fallisce */ });
    }, 400);
    return () => clearTimeout(t);
  }, [isOpen, clientForm.taxCode, clientForm.vatNumber, clientForm.fullName, editingClient?.id]);
  
  // Validate on blur
  const handleBlur = (field) => {
    if (field === "taxCode" && clientForm.taxCode) {
      const result = validateCFForClientType(clientForm.taxCode);
      if (!result.valid) {
        setErrors(prev => ({ ...prev, taxCode: result.error }));
      }
    }
    if (field === "vatNumber" && clientForm.vatNumber) {
      const result = validatePartitaIVA(clientForm.vatNumber);
      if (!result.valid) {
        setErrors(prev => ({ ...prev, vatNumber: result.error }));
      }
    }
  };
  
  // CAP (ZIP) lookup — chiamato onBlur o quando vengono digitate
  // 5 cifre. Politica di auto-fill (28/02/2026):
  //  • 1 match unico AND città/provincia VUOTE  → auto-fill silenzioso
  //  • 1 match unico AND città/provincia già compilate dall'operatore
  //    → NON sovrascriviamo: l'operatore ha già fatto una scelta esplicita.
  //  • più match (CAP che mappa più comuni) → suggestion list selezionabile
  //  • nessun match → warning non bloccante (no auto-fill)
  const lookupCap = useCallback(async (cap) => {
    const clean = (cap || "").replace(/\D/g, "");
    if (clean.length !== 5) {
      setCapSuggestions([]);
      setCapWarning("");
      return;
    }
    if (clean === capLastLookup) return; // debounce stesso valore
    setCapLastLookup(clean);
    try {
      const data = await api.lookupCap(clean);
      if (!data?.found || !Array.isArray(data.matches) || data.matches.length === 0) {
        setCapSuggestions([]);
        setCapWarning(`Nessun comune trovato per il CAP ${clean}. Inserisci Città e Provincia manualmente.`);
        return;
      }
      setCapWarning("");
      if (data.matches.length === 1) {
        // Match unico: auto-fill solo se Città è vuota (non sovrascriviamo input manuale)
        const only = data.matches[0];
        const cityEmpty = !((clientForm.city || "").trim());
        const provEmpty = !((clientForm.province || "").trim());
        if (cityEmpty && provEmpty) {
          setClientForm(prev => ({ ...prev, city: only.city, province: only.province }));
          setCapSuggestions([]);
        } else {
          // mostra come "suggerimento" senza forzare
          setCapSuggestions([only]);
        }
      } else {
        // Più match: lista selezionabile, no auto-fill
        setCapSuggestions(data.matches);
      }
    } catch (err) {
      // Errori di rete → silenzia, l'operatore può digitare manualmente
      console.warn("CAP lookup failed:", err?.message || err);
    }
  }, [capLastLookup, clientForm.city, clientForm.province, setClientForm]);

  // Applica una suggestion (click utente). Sovrascrive sempre i campi
  // perché è una scelta esplicita.
  const applyCapSuggestion = useCallback((sug) => {
    setClientForm(prev => ({ ...prev, city: sug.city, province: sug.province }));
    setCapSuggestions([]);
    setCapWarning("");
  }, [setClientForm]);

  // Wrapper for save that validates first
  const handleSave = () => {
    const newErrors = {};

    // Block-on-duplicate (26/02/2026): se il backend ha rilevato un
    // match BLOCCANTE su taxCode/vatNumber, non procedere col save.
    if (dupBlocking) {
      toast.error("Cliente già presente con stesso Codice Fiscale o Partita IVA. Apri la scheda esistente o modifica i dati inseriti.");
      return;
    }

    // Validate CF if present (using client-type-specific validation)
    if (clientForm.taxCode) {
      const cfResult = validateCFForClientType(clientForm.taxCode);
      if (!cfResult.valid) {
        newErrors.taxCode = cfResult.error;
      }
    }
    
    // Validate P.IVA if present and visible
    if (showBusinessFields && clientForm.vatNumber) {
      const pivaResult = validatePartitaIVA(clientForm.vatNumber);
      if (!pivaResult.valid) {
        newErrors.vatNumber = pivaResult.error;
      }
    }
    
    if (Object.keys(newErrors).length > 0) {
      setErrors(newErrors);
      return;
    }
    
    // Build legacy address field from components
    const addressParts = [];
    if (clientForm.street) addressParts.push(clientForm.street);
    if (clientForm.postalCode) addressParts.push(clientForm.postalCode);
    if (clientForm.city) addressParts.push(clientForm.city);
    if (clientForm.province) addressParts.push(`(${clientForm.province})`);
    
    const updatedForm = {
      ...clientForm,
      address: addressParts.join(', ') || clientForm.address
    };
    
    setClientForm(updatedForm);
    onSave();
  };
  
  // Dirty-tracking guard: confirm before discarding unsaved client data.
  const isDirty = useFormDirty(clientForm, isOpen);

  // Reset errors when dialog closes — with confirmation if data is dirty
  const handleOpenChange = (open) => {
    if (!open && !confirmDirtyClose(isDirty)) return;
    if (!open) {
      setErrors({});
      setCfAnalysis(null);
    }
    onOpenChange(open);
  };

  const handleAnnullaClick = () => {
    if (!confirmDirtyClose(isDirty)) return;
    onReset();
  };
  
  // Document expiry status
  const getDocumentExpiryStatus = () => {
    if (!clientForm.documentExpiryDate) return null;
    
    const today = new Date();
    const expiryParts = clientForm.documentExpiryDate.split('/');
    if (expiryParts.length !== 3) return null;
    
    const expiryDate = new Date(expiryParts[2], expiryParts[1] - 1, expiryParts[0]);
    const daysUntilExpiry = Math.ceil((expiryDate - today) / (1000 * 60 * 60 * 24));
    
    if (daysUntilExpiry < 0) {
      return { status: 'expired', message: 'Documento scaduto', color: 'red' };
    } else if (daysUntilExpiry <= 60) {
      return { status: 'warning', message: `Scadenza tra ${daysUntilExpiry} giorni`, color: 'orange' };
    }
    return { status: 'valid', message: 'Documento valido', color: 'green' };
  };
  
  const docStatus = getDocumentExpiryStatus();
  
  return (
    <Dialog open={isOpen} onOpenChange={handleOpenChange}>
      <DialogTrigger asChild>
        <Button
          data-testid="new-client-button"
          variant="outline"
          size="sm"
          className="gis-border-green gis-text-green hover:bg-[var(--gis-green-50)] hover:scale-105 shadow-sm transition-all duration-200"
          onClick={() => {
            onReset();
            onOpenChange(true);
          }}
          disabled={isSaving}
        >
          <Users className="mr-2 h-4 w-4" />
          Nuovo Cliente
        </Button>
      </DialogTrigger>
      {/* v1.9.1 · 22/07/2026 (bugfix): dialog spesso più alto del viewport
          → footer inaccessibile senza zoom-out. Rimediato con altezza max
          95vh + overflow-y-auto sul body (footer resta visibile perché
          l'overflow è confinato al pannello contenuti). */}
      <DialogContent className="max-w-3xl p-0 gap-0 overflow-hidden max-h-[95vh] flex flex-col" data-testid="client-dialog-content">
        <div className="h-1.5 w-full shrink-0" style={{ backgroundColor: "var(--gis-green)" }} aria-hidden="true" />
        <div className="px-6 pt-4 pb-6 space-y-4 overflow-y-auto flex-1 min-h-0" data-testid="client-dialog-body">
        <DialogHeader>
          <DialogTitle className="gis-heading" style={{ color: "var(--gis-green)" }}>
            {editingClient ? "Modifica Cliente" : "Nuovo Cliente"}
          </DialogTitle>
        </DialogHeader>

        {/* Duplicate detection warning panel (26/02/2026).
            Mostrato se dupMatches.length > 0. Style:
              - rosso/blocking se match su taxCode|vatNumber
              - giallo/warning se match solo su fullName.
        */}
        {dupMatches.length > 0 && (
          <div
            className={cn(
              "rounded-md p-3 border text-sm space-y-2",
              dupBlocking
                ? "bg-red-50 border-red-300 text-red-900"
                : "bg-amber-50 border-amber-300 text-amber-900"
            )}
            data-testid="client-duplicate-warning"
            role="alert"
          >
            <div className="flex items-start gap-2">
              <AlertCircle className={cn("h-4 w-4 mt-0.5 shrink-0", dupBlocking ? "text-red-600" : "text-amber-600")} />
              <div className="flex-1 space-y-2">
                <p className="font-semibold">
                  {dupBlocking
                    ? "Cliente già presente con stesso Codice Fiscale o Partita IVA. Apri la scheda esistente o modifica i dati inseriti."
                    : "Attenzione: cliente già presente in anagrafica. Verifica prima di procedere."}
                </p>
                <ul className="space-y-1">
                  {dupMatches.slice(0, 5).map((m) => (
                    <li key={m.id} className="text-xs flex items-center gap-2">
                      <span className="font-mono px-1.5 py-0.5 rounded bg-white border text-slate-700">
                        {m.matchedOn === "taxCode" ? "CF" : m.matchedOn === "vatNumber" ? "P.IVA" : "Nome"}
                      </span>
                      <span className="font-semibold">{m.fullName || "—"}</span>
                      {m.taxCode && <span className="text-slate-600">· CF: {m.taxCode}</span>}
                      {m.vatNumber && <span className="text-slate-600">· P.IVA: {m.vatNumber}</span>}
                    </li>
                  ))}
                </ul>
              </div>
            </div>
          </div>
        )}
        <div className="space-y-6 py-4">
          {/* Section: Basic Info */}
          <div className="space-y-4">
            <h3 className="text-sm font-semibold text-slate-700 border-b pb-2">Informazioni Base</h3>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              {/* Management Type - Required - DEFAULT: agenzia */}
              <div>
                <Label htmlFor="managementType">Tipo Gestione *</Label>
                <Select
                  value={clientForm.managementType || "agenzia"}
                  onValueChange={(value) =>
                    setClientForm({ ...clientForm, managementType: value })
                  }
                >
                  <SelectTrigger data-testid="client-management-type-select">
                    <SelectValue placeholder="Seleziona tipo gestione" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="studio">Gestione Studio</SelectItem>
                    <SelectItem value="agenzia">Gestione Agenzia</SelectItem>
                  </SelectContent>
                </Select>
              </div>

              {/* Client Type */}
              <div>
                <Label htmlFor="clientType">Tipologia Cliente *</Label>
                <Select
                  value={clientForm.clientType}
                  onValueChange={(value) => {
                    // Reconcile name fields when switching between Azienda and person types.
                    const updates = { ...clientForm, clientType: value };
                    if (value === "Azienda") {
                      // Going to Azienda: drop the split, keep fullName
                      updates.firstName = "";
                      updates.lastName = "";
                    } else if (!clientForm.firstName && !clientForm.lastName && clientForm.fullName) {
                      // Going from Azienda/empty to person-type: split fullName "Cognome Nome"
                      const parts = String(clientForm.fullName).trim().split(/\s+/);
                      updates.lastName = parts[0] || "";
                      updates.firstName = parts.slice(1).join(" ");
                    }
                    setClientForm(updates);
                  }}
                >
                  <SelectTrigger data-testid="client-type-select">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="Azienda">Azienda</SelectItem>
                    <SelectItem value="Ditta individuale">Ditta individuale</SelectItem>
                    <SelectItem value="Professionista">Professionista</SelectItem>
                    <SelectItem value="Privato">Privato</SelectItem>
                  </SelectContent>
                </Select>
              </div>

              {/* Full Name / Company Name — split into Cognome+Nome for person types,
                  single "Ragione Sociale" for Azienda */}
              {clientForm.clientType === "Azienda" ? (
                <div className="md:col-span-2">
                  <Label htmlFor="fullName">Ragione Sociale *</Label>
                  <Input
                    id="fullName"
                    data-testid="client-full-name-input"
                    value={clientForm.fullName}
                    onChange={(e) =>
                      handleFieldUpdate(clientForm, setClientForm, "fullName", e.target.value)
                    }
                    placeholder="Es. Studio Alfa Srl"
                  />
                </div>
              ) : (
                <>
                  <div>
                    <Label htmlFor="lastName">Cognome *</Label>
                    <Input
                      id="lastName"
                      data-testid="client-last-name-input"
                      value={clientForm.lastName || ""}
                      onChange={(e) => {
                        const lastName = e.target.value;
                        const composed = `${lastName} ${clientForm.firstName || ""}`.replace(/\s+/g, " ").trim();
                        setClientForm({ ...clientForm, lastName, fullName: composed });
                      }}
                      placeholder="Es. Rossi"
                    />
                  </div>
                  <div>
                    <Label htmlFor="firstName">Nome *</Label>
                    <Input
                      id="firstName"
                      data-testid="client-first-name-input"
                      value={clientForm.firstName || ""}
                      onChange={(e) => {
                        const firstName = e.target.value;
                        const composed = `${clientForm.lastName || ""} ${firstName}`.replace(/\s+/g, " ").trim();
                        setClientForm({ ...clientForm, firstName, fullName: composed });
                      }}
                      placeholder="Es. Mario"
                    />
                  </div>
                </>
              )}
            </div>
          </div>

          {/* Section: Fiscal Data */}
          <div className="space-y-4">
            <h3 className="text-sm font-semibold text-slate-700 border-b pb-2">Dati Fiscali</h3>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              {/* Codice Fiscale */}
              <div>
                <Label htmlFor="taxCode">Codice Fiscale</Label>
                <Input
                  id="taxCode"
                  data-testid="client-tax-code-input"
                  value={clientForm.taxCode}
                  onChange={(e) => {
                    let value = e.target.value;
                    // For Azienda: only allow numeric input
                    if (isCompanyType) {
                      value = value.replace(/\D/g, '');
                    }
                    handleFieldUpdate(clientForm, setClientForm, "taxCode", value, setErrors);
                    // Auto-copy CF into P.IVA for Azienda
                    if (isCompanyType) {
                      const numVal = value.replace(/\D/g, '');
                      setClientForm(prev => ({ ...prev, taxCode: numVal, vatNumber: numVal }));
                    }
                  }}
                  onBlur={() => handleBlur("taxCode")}
                  placeholder={isCompanyType ? "12345678901" : "RSSMRA80A01H501U"}
                  maxLength={isCompanyType ? 11 : 16}
                  className={cn("font-mono uppercase", errors.taxCode && "border-red-500")}
                />
                {errors.taxCode && (
                  <p className="text-xs text-red-500 mt-1 flex items-center gap-1">
                    <AlertCircle className="h-3 w-3" />
                    {errors.taxCode}
                  </p>
                )}
                {/* CF Analysis feedback - only show for non-company types */}
                {cfAnalysis && !errors.taxCode && !isCompanyType && (
                  <div className="mt-1 space-y-1">
                    {cfAnalysis.coherence && !cfAnalysis.coherence.coherent && (
                      <p className="text-xs text-orange-600 flex items-center gap-1">
                        <AlertCircle className="h-3 w-3" />
                        {cfAnalysis.coherence.message}
                      </p>
                    )}
                    {cfAnalysis.birthDate && (
                      <p className="text-xs text-green-600 flex items-center gap-1">
                        <CheckCircle2 className="h-3 w-3" />
                        Data nascita estratta: {cfAnalysis.birthDate}
                      </p>
                    )}
                    {cfAnalysis.birthPlace && (
                      <p className="text-xs text-green-600 flex items-center gap-1">
                        <CheckCircle2 className="h-3 w-3" />
                        Comune nascita: {cfAnalysis.birthPlace}
                      </p>
                    )}
                    {/* Check consistenza Comune inserito ↔ CF (17/07/2026 · v1.4.1).
                        Silenzioso in stato "skip"/"unknown". Rende:
                          - VERDE se il Comune inserito manualmente combacia con quello del CF
                          - ARANCIONE se differisce (typo probabile) */}
                    {cfAnalysis.birthPlaceCheck?.status === 'match' && (
                      <p
                        className="text-xs text-green-600 flex items-center gap-1"
                        data-testid="cf-birthplace-match"
                      >
                        <CheckCircle2 className="h-3 w-3" />
                        {cfAnalysis.birthPlaceCheck.message}
                      </p>
                    )}
                    {cfAnalysis.birthPlaceCheck?.status === 'mismatch' && (
                      <p
                        className="text-xs text-orange-600 flex items-center gap-1"
                        data-testid="cf-birthplace-mismatch"
                      >
                        <AlertCircle className="h-3 w-3" />
                        {cfAnalysis.birthPlaceCheck.message}
                      </p>
                    )}
                  </div>
                )}
              </div>

              {/* Partita IVA */}
              <div className={showBusinessFields ? "" : "hidden"}>
                <Label htmlFor="vatNumber">Partita IVA</Label>
                <div className="flex gap-2">
                  <Input
                    id="vatNumber"
                    data-testid="client-vat-number-input"
                    value={clientForm.vatNumber}
                    onChange={(e) =>
                      handleFieldUpdate(clientForm, setClientForm, "vatNumber", e.target.value, setErrors)
                    }
                    onBlur={() => handleBlur("vatNumber")}
                    placeholder="12345678901"
                    maxLength={11}
                    className={cn("font-mono flex-1", errors.vatNumber && "border-red-500")}
                  />
                  {/* Button to copy P.IVA to CF for Azienda */}
                  {isCompanyType && clientForm.vatNumber && clientForm.vatNumber.length === 11 && (
                    <Button
                      type="button"
                      variant="outline"
                      size="sm"
                      data-testid="copy-vat-to-cf-button"
                      onClick={() => {
                        handleFieldUpdate(clientForm, setClientForm, "taxCode", clientForm.vatNumber, setErrors);
                      }}
                      title="Copia P.IVA in Codice Fiscale"
                      className="shrink-0"
                    >
                      <Copy className="h-4 w-4 mr-1" />
                      Usa come CF
                    </Button>
                  )}
                </div>
                {errors.vatNumber && (
                  <p className="text-xs text-red-500 mt-1 flex items-center gap-1">
                    <AlertCircle className="h-3 w-3" />
                    {errors.vatNumber}
                  </p>
                )}
                {/* Auto-populate hint for Azienda */}
                {isCompanyType && clientForm.vatNumber && clientForm.vatNumber.length === 11 && !clientForm.taxCode && (
                  <p className="text-xs text-blue-600 mt-1">
                    Per le aziende, CF e P.IVA sono spesso identici. Usa il bottone per copiare.
                  </p>
                )}
              </div>

              {/* Birth Date (auto-filled from CF) - hidden for Azienda */}
              {!isCompanyType && (
              <div>
                <Label htmlFor="birthDate">Data di Nascita</Label>
                <Input
                  id="birthDate"
                  data-testid="client-birth-date-input"
                  value={clientForm.birthDate || ''}
                  onChange={(e) =>
                    setClientForm({ ...clientForm, birthDate: e.target.value })
                  }
                  placeholder="GG/MM/AAAA"
                />
              </div>
              )}

              {/* Birth Place (auto-filled from CF) - hidden for Azienda */}
              {!isCompanyType && (
              <div>
                <Label htmlFor="birthPlace">Comune di Nascita</Label>
                <Input
                  id="birthPlace"
                  data-testid="client-birth-place-input"
                  value={clientForm.birthPlace || ''}
                  onChange={(e) =>
                    handleFieldUpdate(clientForm, setClientForm, "birthPlace", e.target.value)
                  }
                  placeholder="Es. Civitavecchia (RM)"
                />
              </div>
              )}

              {/* SDI Code */}
              <div className={showBusinessFields ? "" : "hidden"}>
                <Label htmlFor="sdiCode">Codice SDI</Label>
                <Input
                  id="sdiCode"
                  data-testid="client-sdi-code-input"
                  value={clientForm.sdiCode}
                  onChange={(e) =>
                    setClientForm({ ...clientForm, sdiCode: e.target.value.toUpperCase() })
                  }
                  placeholder="KRRH6B9"
                  maxLength={7}
                />
              </div>

              {/* PEC */}
              <div className={showBusinessFields ? "" : "hidden"}>
                <Label htmlFor="pec">PEC</Label>
                <Input
                  id="pec"
                  type="email"
                  data-testid="client-pec-input"
                  value={clientForm.pec}
                  onChange={(e) =>
                    setClientForm({ ...clientForm, pec: e.target.value.toLowerCase() })
                  }
                  placeholder="azienda@pec.it"
                />
              </div>

              {/* REA Registration Code (Azienda + Ditta individuale only) */}
              {showReaField && (
                <div>
                  <Label htmlFor="reaCode">N. iscrizione REA</Label>
                  <Input
                    id="reaCode"
                    data-testid="client-rea-code-input"
                    value={clientForm.reaCode || ""}
                    onChange={(e) =>
                      setClientForm({ ...clientForm, reaCode: e.target.value.toUpperCase() })
                    }
                    placeholder="RM-1626139"
                    maxLength={20}
                  />
                  <p className="text-xs text-slate-500 mt-1">Campo opzionale.</p>
                </div>
              )}
            </div>

            {/* SDI/PEC Info Box */}
            {showBusinessFields && (
              <div className="bg-blue-50 border border-blue-200 rounded-lg p-3">
                <div className="flex items-start gap-2">
                  <Info className="h-4 w-4 text-blue-600 mt-0.5 shrink-0" />
                  <p className="text-xs text-blue-800">
                    <strong>Fatturazione elettronica:</strong> Se il codice SDI è compilato, verrà usato per la fatturazione. 
                    Altrimenti verrà utilizzata la PEC.
                  </p>
                </div>
              </div>
            )}

            {/* Split Payment — art. 17-ter DPR 633/1972 (07/06/2026).
                Attivare per enti pubblici e società a controllo pubblico:
                l'IVA in fattura sarà esposta ma non incassata. */}
            {showBusinessFields && (
              <div className="bg-cyan-50 border border-cyan-200 rounded-lg p-3">
                <label className="flex items-start gap-3 cursor-pointer">
                  <input
                    type="checkbox"
                    data-testid="client-split-payment-checkbox"
                    checked={!!clientForm.splitPaymentEnabled}
                    onChange={(e) =>
                      setClientForm({ ...clientForm, splitPaymentEnabled: e.target.checked })
                    }
                    className="mt-0.5 h-4 w-4 rounded border-cyan-400 text-cyan-600 focus:ring-cyan-500"
                  />
                  <div className="text-xs">
                    <div className="font-semibold text-cyan-900">
                      Applica Split Payment (art. 17-ter DPR 633/1972)
                    </div>
                    <p className="text-cyan-800 mt-1">
                      Attiva per enti pubblici e società a controllo pubblico:
                      l&apos;IVA verrà esposta in fattura ma <strong>non incassata</strong> dal
                      fornitore (versata direttamente all&apos;Erario dal committente). Il netto
                      a pagare sarà calcolato come Totale - IVA.
                    </p>
                  </div>
                </label>
              </div>
            )}
          </div>

          {/* Section: Contact */}
          <div className="space-y-4">
            <h3 className="text-sm font-semibold text-slate-700 border-b pb-2">Contatti</h3>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              {/* Email */}
              <div>
                <Label htmlFor="email">Email</Label>
                <Input
                  id="email"
                  type="email"
                  data-testid="client-email-input"
                  value={clientForm.email}
                  onChange={(e) =>
                    setClientForm({ ...clientForm, email: e.target.value })
                  }
                  placeholder="email@esempio.it"
                />
              </div>

              {/* Phone */}
              <div>
                <Label htmlFor="phone">Telefono</Label>
                <Input
                  id="phone"
                  data-testid="client-phone-input"
                  value={clientForm.phone}
                  onChange={(e) =>
                    handleFieldUpdate(clientForm, setClientForm, "phone", e.target.value)
                  }
                  placeholder="333 123 4567"
                />
              </div>
            </div>
          </div>

          {/* Section: Address (Split fields) */}
          <div className="space-y-4">
            <h3 className="text-sm font-semibold text-slate-700 border-b pb-2">Indirizzo</h3>
            <div className="grid grid-cols-1 md:grid-cols-6 gap-4">
              {/* Street */}
              <div className="md:col-span-4">
                <Label htmlFor="street">Indirizzo</Label>
                <Input
                  id="street"
                  data-testid="client-street-input"
                  value={clientForm.street || ''}
                  onChange={(e) =>
                    handleFieldUpdate(clientForm, setClientForm, "street", e.target.value)
                  }
                  placeholder="Via Roma, 123"
                />
              </div>

              {/* CAP */}
              <div className="md:col-span-2">
                <Label htmlFor="postalCode">CAP</Label>
                <Input
                  id="postalCode"
                  data-testid="client-postal-code-input"
                  value={clientForm.postalCode || ''}
                  onChange={(e) => {
                    handleFieldUpdate(clientForm, setClientForm, "postalCode", e.target.value);
                    // Quando l'operatore raggiunge 5 cifre, fai lookup automatica
                    const digits = (e.target.value || "").replace(/\D/g, "");
                    if (digits.length === 5) lookupCap(digits);
                  }}
                  onBlur={(e) => lookupCap(e.target.value)}
                  placeholder="00053"
                  maxLength={5}
                  inputMode="numeric"
                />
                {/* Warning non-bloccante: CAP non trovato in archivio */}
                {capWarning && (
                  <p
                    className="mt-1 text-xs text-amber-700 bg-amber-50 border border-amber-200 rounded px-2 py-1"
                    data-testid="cap-warning-msg"
                  >
                    {capWarning}
                  </p>
                )}
              </div>

              {/* City */}
              <div className="md:col-span-4">
                <Label htmlFor="city">Città</Label>
                <Input
                  id="city"
                  data-testid="client-city-input"
                  value={clientForm.city || ''}
                  onChange={(e) =>
                    handleFieldUpdate(clientForm, setClientForm, "city", e.target.value)
                  }
                  placeholder="Civitavecchia"
                />
              </div>

              {/* Province */}
              <div className="md:col-span-2">
                <Label htmlFor="province">Provincia</Label>
                <Input
                  id="province"
                  data-testid="client-province-input"
                  value={clientForm.province || ''}
                  onChange={(e) =>
                    handleFieldUpdate(clientForm, setClientForm, "province", e.target.value)
                  }
                  placeholder="RM"
                  maxLength={2}
                  className="uppercase"
                />
              </div>

              {/* CAP multi-match suggestion list (28/02/2026):
                  appare quando un CAP mappa più comuni o quando l'operatore
                  ha già compilato Città/Provincia e c'è un match per evitare
                  di sovrascrivere senza conferma esplicita. */}
              {capSuggestions.length > 0 && (
                <div className="md:col-span-8" data-testid="cap-suggestion-list">
                  <p className="text-xs text-slate-600 mb-1">
                    {capSuggestions.length > 1
                      ? "Più comuni mappati su questo CAP. Seleziona quello corretto:"
                      : "Comune suggerito per questo CAP:"}
                  </p>
                  <div className="flex flex-wrap gap-2">
                    {capSuggestions.map((s, idx) => (
                      <button
                        key={`${s.city}-${s.province}-${idx}`}
                        type="button"
                        onClick={() => applyCapSuggestion(s)}
                        data-testid={`cap-suggestion-${idx}`}
                        className="text-xs px-3 py-1 rounded-full border border-blue-300 bg-blue-50 text-blue-700 hover:bg-blue-100 hover:border-blue-400 transition-colors"
                      >
                        {s.city} ({s.province})
                      </button>
                    ))}
                  </div>
                </div>
              )}
            </div>
          </div>

          {/* Section: Identity Document - hidden for Azienda */}
          {!isCompanyType && (
          <div className="space-y-4">
            <div className="flex items-center justify-between border-b pb-2">
              <h3 className="text-sm font-semibold text-slate-700 flex items-center gap-2">
                <CreditCard className="h-4 w-4" />
                Documento di Identità
              </h3>
              {docStatus && (
                <span className={cn(
                  "text-xs px-2 py-1 rounded-full flex items-center gap-1",
                  docStatus.status === 'expired' && "bg-red-100 text-red-700",
                  docStatus.status === 'warning' && "bg-orange-100 text-orange-700",
                  docStatus.status === 'valid' && "bg-green-100 text-green-700"
                )}>
                  {docStatus.status === 'expired' && <AlertCircle className="h-3 w-3" />}
                  {docStatus.status === 'warning' && <FileWarning className="h-3 w-3" />}
                  {docStatus.status === 'valid' && <CheckCircle2 className="h-3 w-3" />}
                  {docStatus.message}
                </span>
              )}
            </div>
            <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
              {/* Document Type */}
              <div>
                <Label htmlFor="documentType">Tipo Documento</Label>
                <Select
                  value={clientForm.documentType || ""}
                  onValueChange={(value) =>
                    setClientForm({ ...clientForm, documentType: value })
                  }
                >
                  <SelectTrigger data-testid="client-document-type-select">
                    <SelectValue placeholder="Seleziona..." />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="CI">Carta d'Identità</SelectItem>
                    <SelectItem value="Passaporto">Passaporto</SelectItem>
                    <SelectItem value="Patente">Patente di Guida</SelectItem>
                  </SelectContent>
                </Select>
              </div>

              {/* Document Number */}
              <div className="md:col-span-2">
                <Label htmlFor="documentNumber">Numero Documento</Label>
                <Input
                  id="documentNumber"
                  data-testid="client-document-number-input"
                  value={clientForm.documentNumber || ''}
                  onChange={(e) =>
                    handleFieldUpdate(clientForm, setClientForm, "documentNumber", e.target.value)
                  }
                  placeholder="AA1234567"
                  className="uppercase"
                />
              </div>

              {/* Document Issuer */}
              <div className="md:col-span-3">
                <Label htmlFor="documentIssuer">Ente Rilascio</Label>
                <Input
                  id="documentIssuer"
                  data-testid="client-document-issuer-input"
                  value={clientForm.documentIssuer || ''}
                  onChange={(e) =>
                    setClientForm({ ...clientForm, documentIssuer: e.target.value })
                  }
                  placeholder="Comune di Civitavecchia"
                />
              </div>

              {/* Document Issue Date */}
              <div>
                <Label htmlFor="documentIssueDate">Data Rilascio</Label>
                <Input
                  id="documentIssueDate"
                  data-testid="client-document-issue-date-input"
                  value={clientForm.documentIssueDate || ''}
                  onChange={(e) =>
                    handleFieldUpdate(clientForm, setClientForm, "documentIssueDate", e.target.value)
                  }
                  placeholder="GG/MM/AAAA"
                  maxLength={10}
                />
              </div>

              {/* Document Expiry Date */}
              <div className="md:col-span-2">
                <Label htmlFor="documentExpiryDate">Data Scadenza</Label>
                <Input
                  id="documentExpiryDate"
                  data-testid="client-document-expiry-date-input"
                  value={clientForm.documentExpiryDate || ''}
                  onChange={(e) =>
                    handleFieldUpdate(clientForm, setClientForm, "documentExpiryDate", e.target.value)
                  }
                  placeholder="GG/MM/AAAA"
                  maxLength={10}
                  className={cn(
                    docStatus?.status === 'expired' && "border-red-500",
                    docStatus?.status === 'warning' && "border-orange-500"
                  )}
                />
              </div>
            </div>
          </div>
          )}

          {/* Section: Notes */}
          <div className="space-y-4">
            <h3 className="text-sm font-semibold text-slate-700 border-b pb-2">Note</h3>
            <div>
              <Textarea
                id="notes"
                data-testid="client-notes-textarea"
                value={clientForm.notes}
                onChange={(e) =>
                  setClientForm({ ...clientForm, notes: e.target.value })
                }
                rows={3}
                placeholder="Note aggiuntive sul cliente..."
              />
            </div>
          </div>
        </div>
        </div>
        {/* Footer sticky fuori dal body scrollabile: sempre visibile. */}
        <DialogFooter className="shrink-0 border-t border-slate-100 px-6 py-3 bg-white">
          {editingClient?.id && (
            <Button
              variant="outline"
              onClick={() => setIs360Open(true)}
              className="mr-auto border-indigo-300 text-indigo-700 hover:bg-indigo-50 hover:border-indigo-400"
              data-testid="open-client-360-button"
              title="Storico aggregato di tutte le pratiche del cliente"
            >
              <Sparkles className="mr-2 h-4 w-4" />
              Vista 360°
            </Button>
          )}
          <Button variant="outline" onClick={handleAnnullaClick} data-testid="cancel-client-button">
            Annulla
          </Button>
          <Button
            data-testid="save-client-button"
            onClick={handleSave}
            disabled={dupBlocking}
            className="gis-btn-primary"
            title={dupBlocking ? "Salvataggio bloccato: cliente duplicato (CF o P.IVA)" : ""}
          >
            {editingClient ? "Aggiorna" : "Salva Cliente"}
          </Button>
        </DialogFooter>
      </DialogContent>
      {/* Cliente 360° — modale indipendente, aperta on-demand */}
      {editingClient?.id && (
        <Client360Dialog
          isOpen={is360Open}
          onOpenChange={setIs360Open}
          clientId={editingClient.id}
          clientName={editingClient?.fullName || clientForm?.fullName}
        />
      )}
    </Dialog>
  );
}

